import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  UserCircle2,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CalendarX,
  Search,
  Eye,
  EyeOff,
  Mail,
  Phone as PhoneIcon,
  Clock,
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  Copy,
  Calendar as CalendarIcon,
  RefreshCw,
  Link as LinkIcon,
  Unlink,
  CheckCircle2,
  ArrowUp,
  ArrowDown,
  Upload,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useLocation } from "@/contexts/LocationContext";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";

interface Service {
  id: string;
  name: string;
  durationMinutes: number;
  price: number;
  color: string;
}

interface SpecialistService {
  id: string;
  serviceId: string;
  customDurationMinutes: number | null;
  customPrice: number | null;
  service?: Service;
}

interface Specialist {
  id: string;
  organizationId: string;
  locationId: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  countryCode: string;
  color: string;
  avatar: string | null;
  bio: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  services: SpecialistService[];
}

interface FormData {
  locationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  color: string;
  bio: string;
  isActive: boolean;
  serviceIds: string[];
}

interface Shift {
  start: string;
  end: string;
}

interface DayConfig {
  dayOfWeek: number;
  isWorking: boolean;
  shifts: Shift[];
}

const DAY_NAMES = [
  "Domingo",
  "Lunes",
  "Martes",
  "Mi\u00e9rcoles",
  "Jueves",
  "Viernes",
  "S\u00e1bado",
];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const SHIFT_AM: Shift = { start: "09:00", end: "13:00" };
const SHIFT_PM: Shift = { start: "16:00", end: "20:00" };

function defaultDays(): DayConfig[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    isWorking: dayOfWeek !== 0,
    shifts: dayOfWeek === 0 ? [] : [{ ...SHIFT_AM }, { ...SHIFT_PM }],
  }));
}

const COLOR_PRESETS = [
  "#6366f1", // indigo
  "#10b981", // emerald
  "#ec4899", // pink
  "#f59e0b", // amber
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ef4444", // red
  "#14b8a6", // teal
];

const buildEmptyForm = (locationId = ""): FormData => ({
  locationId,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  countryCode: "+34",
  color: "#6366f1",
  bio: "",
  isActive: true,
  serviceIds: [],
});

// Formatos aceptados para la foto del profesional (mismos que acepta el logo
// de una sede en Locations.tsx).
const VALID_PHOTO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];

// La subida de foto usa el MISMO transporte que el logo de sede (Locations.tsx):
// un data URI base64 en JSON `{ logo }`, NO multipart. El backend
// (POST /specialists/:id/photo) espera exactamente esa forma.
const fileToDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("No se pudo leer la imagen"));
    reader.readAsDataURL(file);
  });

export default function Specialists() {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("servicios:editar");
  const { locations, currentLocationId } = useLocation();

  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<{
    message: string;
    isBookingModeMismatch: boolean;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(buildEmptyForm());
  const [scheduleDays, setScheduleDays] = useState<DayConfig[]>(defaultDays());
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  // Foto del profesional (modal crear/editar). En edición se sube al
  // instante contra POST /specialists/:id/photo (ya hay id); en creación no
  // hay id todavía, así que el archivo se guarda en `modalPhotoFile` y se
  // sube justo después del POST /specialists que crea al profesional (ver
  // `handleSubmit`). `modalAvatarUrl` alimenta la vista previa circular en
  // ambos casos (URL persistida en edición, data URL local en creación).
  const [modalAvatarUrl, setModalAvatarUrl] = useState<string | null>(null);
  const [modalPhotoFile, setModalPhotoFile] = useState<File | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // ============================================================
  // Google Calendar por especialista
  // ============================================================
  interface GcStatus {
    connected: boolean;
    googleEmail?: string | null;
    googleCalendarId?: string | null;
    syncStatus?: string | null;
    lastError?: string | null;
    lastSyncAt?: string | null;
  }
  const [gcStatus, setGcStatus] = useState<Record<string, GcStatus>>({});
  const [gcBusy, setGcBusy] = useState<Record<string, string>>({});
  const [inviteModal, setInviteModal] = useState<{
    specialist: Specialist;
    url: string;
    expiresInMinutes: number;
  } | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle"); // id -> 'connect'|'sync'|'disconnect'

  const loadGcStatus = async (specialistIds: string[]) => {
    if (specialistIds.length === 0) return;
    try {
      const results = await Promise.all(
        specialistIds.map((id) =>
          apiClient
            .get(`/specialists/${id}/google-calendar`)
            .then((r) => ({ id, data: r.data?.data as GcStatus }))
            .catch(() => ({ id, data: { connected: false } as GcStatus })),
        ),
      );
      setGcStatus((prev) => {
        const next = { ...prev };
        for (const r of results) next[r.id] = r.data;
        return next;
      });
    } catch {
      // silencioso
    }
  };

  const refreshGcStatus = async (specialistId: string) => {
    try {
      const r = await apiClient.get(
        `/specialists/${specialistId}/google-calendar`,
      );
      setGcStatus((prev) => ({ ...prev, [specialistId]: r.data?.data }));
    } catch {
      // silencioso
    }
  };

  const handleConnectGoogle = async (specialistId: string) => {
    try {
      setGcBusy((p) => ({ ...p, [specialistId]: "connect" }));
      const res = await apiClient.get(
        `/specialists/${specialistId}/google-calendar/invite-link`,
      );
      const url = res.data?.data?.url as string | undefined;
      const expiresInMinutes =
        (res.data?.data?.expiresInMinutes as number | undefined) ?? 30;
      if (!url) throw new Error("No se recibió la URL de Google");
      const specialist = specialists.find((s) => s.id === specialistId);
      if (!specialist) throw new Error("Profesional no encontrado");
      setCopyState("idle");
      setInviteModal({ specialist, url, expiresInMinutes });
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo generar el enlace de conexión"),
        "error",
      );
    } finally {
      setGcBusy((p) => {
        const { [specialistId]: _omit, ...rest } = p;
        return rest;
      });
    }
  };

  const copyInviteLink = async () => {
    if (!inviteModal) return;
    try {
      await navigator.clipboard.writeText(inviteModal.url);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2500);
    } catch {
      // Fallback: seleccionar el input
      const input = document.getElementById(
        "invite-link-input",
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    }
  };

  const sendInviteByEmail = () => {
    if (!inviteModal) return;
    const sp = inviteModal.specialist;
    const fullName = `${sp.firstName} ${sp.lastName ?? ""}`.trim();
    const subject = encodeURIComponent(
      "Conecta tu Google Calendar con tus citas",
    );
    const body = encodeURIComponent(
      `Hola ${fullName || ""},\n\n` +
        `Para que tus citas aparezcan automáticamente en tu Google Calendar, ` +
        `entra en este enlace desde el navegador donde tengas tu cuenta de Google:\n\n` +
        `${inviteModal.url}\n\n` +
        `Solo tendrás que aceptar los permisos. No es necesario crear ninguna cuenta.\n\n` +
        `El enlace caduca en ${inviteModal.expiresInMinutes} minutos. Si te caduca, avísame y te genero otro.\n\n` +
        `Gracias.`,
    );
    const to = encodeURIComponent(sp.email ?? "");
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const handleSyncGoogle = async (specialistId: string) => {
    try {
      setGcBusy((p) => ({ ...p, [specialistId]: "sync" }));
      await apiClient.post(`/specialists/${specialistId}/google-calendar/sync`);
      showToast("Sincronización completada");
      await refreshGcStatus(specialistId);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo sincronizar con Google Calendar"),
        "error",
      );
    } finally {
      setGcBusy((p) => {
        const { [specialistId]: _omit, ...rest } = p;
        return rest;
      });
    }
  };

  const handleDisconnectGoogle = async (specialistId: string) => {
    if (
      !(await confirm({
        title: "¿Desconectar Google Calendar?",
        message: "Las citas de este profesional dejarán de sincronizarse.",
        confirmText: "Sí, desconectar",
        danger: true,
      }))
    ) {
      return;
    }
    try {
      setGcBusy((p) => ({ ...p, [specialistId]: "disconnect" }));
      await apiClient.delete(`/specialists/${specialistId}/google-calendar`);
      showToast("Google Calendar desconectado");
      await refreshGcStatus(specialistId);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo desconectar Google Calendar"),
        "error",
      );
    } finally {
      setGcBusy((p) => {
        const { [specialistId]: _omit, ...rest } = p;
        return rest;
      });
    }
  };

  useEffect(() => {
    loadAll();
  }, [showInactive, currentLocationId]);

  const loadAll = async () => {
    setLoadError(null);
    try {
      setIsLoading(true);
      const [specRes, svcRes] = await Promise.all([
        apiClient.get("/specialists", {
          params: {
            includeInactive: showInactive ? "true" : "false",
            ...(currentLocationId ? { locationId: currentLocationId } : {}),
          },
        }),
        apiClient.get("/services", {
          params: { includeInactive: "false" },
        }),
      ]);
      setSpecialists(specRes.data?.data ?? []);
      setServices(svcRes.data?.data ?? []);
      // cargar estado de Google Calendar para cada especialista
      const ids: string[] = (specRes.data?.data ?? []).map(
        (s: Specialist) => s.id,
      );
      if (ids.length > 0) {
        loadGcStatus(ids);
      }
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: unknown } }
      )?.response;
      const status = response?.status;
      const data = response?.data as
        | { code?: string; message?: string }
        | undefined;
      const message = data?.message ?? "";
      const isBookingModeMismatch =
        status === 403 &&
        (data?.code === "BOOKING_MODE_MISMATCH" ||
          message.includes("BOOKING_MODE_MISMATCH"));
      setLoadError({
        message: isBookingModeMismatch
          ? "Esta sección es solo para negocios que trabajan con citas."
          : message || "No se pudieron cargar los profesionales",
        isBookingModeMismatch,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return specialists;
    return specialists.filter((s) => {
      const fullName = `${s.firstName} ${s.lastName ?? ""}`.toLowerCase();
      return (
        fullName.includes(q) ||
        (s.email ?? "").toLowerCase().includes(q) ||
        (s.phone ?? "").includes(q)
      );
    });
  }, [specialists, search]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(buildEmptyForm(currentLocationId ?? ""));
    setScheduleDays(defaultDays());
    setStep(1);
    setModalAvatarUrl(null);
    setModalPhotoFile(null);
    setShowModal(true);
  };

  const openEdit = async (s: Specialist) => {
    setEditingId(s.id);
    setFormData({
      locationId: s.locationId,
      firstName: s.firstName,
      lastName: s.lastName ?? "",
      email: s.email ?? "",
      phone: s.phone ?? "",
      countryCode: s.countryCode || "+34",
      color: s.color,
      bio: s.bio ?? "",
      isActive: s.isActive,
      serviceIds: s.services.map((ss) => ss.serviceId),
    });
    setScheduleDays(defaultDays());
    setStep(1);
    setModalAvatarUrl(s.avatar);
    setModalPhotoFile(null);
    setShowModal(true);
    // Cargar horario actual del profesional
    try {
      const res = await apiClient.get(`/specialists/${s.id}/schedule`);
      const apiDays = (res.data?.data?.days ?? []) as DayConfig[];
      if (Array.isArray(apiDays) && apiDays.length === 7) {
        setScheduleDays(
          apiDays.map((d) => ({
            dayOfWeek: d.dayOfWeek,
            isWorking: !!d.isWorking,
            shifts: Array.isArray(d.shifts) ? d.shifts : [],
          })),
        );
      }
    } catch {
      /* sin horario previo, deja el default */
    }
  };

  /** Selección de archivo para la foto del profesional. En edición (ya hay
   * `editingId`) se sube al instante contra `POST /specialists/:id/photo`,
   * igual que el logo de una sede en `Locations.tsx`. En creación (aún sin
   * id) solo se guarda el archivo y se muestra una vista previa local; la
   * subida real ocurre en `handleSubmit` una vez creado el profesional. */
  const handlePhotoFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!VALID_PHOTO_TYPES.includes(file.type)) {
      showToast(
        "Formato de imagen no válido. Usa PNG, JPEG, GIF o WebP",
        "error",
      );
      if (e.target) e.target.value = "";
      return;
    }

    if (editingId) {
      setPhotoUploading(true);
      try {
        const logo = await fileToDataUri(file);
        const res = await apiClient.post(
          `/specialists/${editingId}/photo`,
          { logo },
        );
        const avatarUrl = (res.data?.data?.avatar as string | undefined) ?? null;
        setModalAvatarUrl(avatarUrl);
        setSpecialists((prev) =>
          prev.map((sp) =>
            sp.id === editingId ? { ...sp, avatar: avatarUrl } : sp,
          ),
        );
        showToast("Foto actualizada");
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo subir la foto"),
          "error",
        );
      } finally {
        setPhotoUploading(false);
      }
    } else {
      // Creación: sin id todavía. Guardamos el archivo y mostramos una vista
      // previa local (data URL) hasta que se cree el profesional.
      setModalPhotoFile(file);
      const reader = new FileReader();
      reader.onload = () => setModalAvatarUrl(reader.result as string);
      reader.readAsDataURL(file);
    }

    if (e.target) e.target.value = "";
  };

  const handleRemovePhoto = async () => {
    if (editingId) {
      if (
        !(await confirm({
          title: "¿Eliminar la foto?",
          message: "Se eliminará la foto de este profesional.",
          confirmText: "Sí, eliminar",
          danger: true,
        }))
      )
        return;
      setPhotoUploading(true);
      try {
        await apiClient.delete(`/specialists/${editingId}/photo`);
        setModalAvatarUrl(null);
        setSpecialists((prev) =>
          prev.map((sp) =>
            sp.id === editingId ? { ...sp, avatar: null } : sp,
          ),
        );
        showToast("Foto eliminada");
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo eliminar la foto"),
          "error",
        );
      } finally {
        setPhotoUploading(false);
      }
    } else {
      // Creación: la foto aún no se ha subido a ningún sitio, solo se quita
      // la selección pendiente y la vista previa local.
      setModalPhotoFile(null);
      setModalAvatarUrl(null);
    }
  };

  const toggleServiceId = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(id)
        ? prev.serviceIds.filter((x) => x !== id)
        : [...prev.serviceIds, id],
    }));
  };

  // Helpers de horario (paso 2)
  const updateDay = (dayOfWeek: number, patch: Partial<DayConfig>) => {
    setScheduleDays((prev) =>
      prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)),
    );
  };
  const updateShift = (
    dayOfWeek: number,
    idx: number,
    patch: Partial<Shift>,
  ) => {
    setScheduleDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? {
              ...d,
              shifts: d.shifts.map((s, i) =>
                i === idx ? { ...s, ...patch } : s,
              ),
            }
          : d,
      ),
    );
  };
  const addShift = (dayOfWeek: number) => {
    setScheduleDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? {
              ...d,
              shifts: [...d.shifts, { start: "09:00", end: "13:00" }],
            }
          : d,
      ),
    );
  };
  const removeShift = (dayOfWeek: number, idx: number) => {
    setScheduleDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.filter((_, i) => i !== idx) }
          : d,
      ),
    );
  };
  const copyMondayToWeek = () => {
    const monday = scheduleDays.find((d) => d.dayOfWeek === 1);
    if (!monday) return;
    setScheduleDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek >= 1 && d.dayOfWeek <= 5
          ? {
              ...d,
              isWorking: monday.isWorking,
              shifts: monday.shifts.map((s) => ({ ...s })),
            }
          : d,
      ),
    );
  };

  const validateStep1 = (): string | null => {
    if (!formData.firstName.trim()) return "El nombre es obligatorio";
    if (!formData.locationId) return "Selecciona una ubicación";
    return null;
  };

  const validateSchedule = (): string | null => {
    for (const d of scheduleDays) {
      if (!d.isWorking) continue;
      for (const s of d.shifts) {
        if (!s.start || !s.end)
          return `Hay un turno incompleto en ${DAY_NAMES[d.dayOfWeek]}`;
        if (s.start >= s.end)
          return `En ${DAY_NAMES[d.dayOfWeek]} la hora de inicio debe ser menor que la de fin`;
      }
    }
    return null;
  };

  // Un profesional sin servicios nunca puede recibir citas (no aparece en
  // ningún selector de reserva), así que exigimos al menos 1 antes de crear
  // o guardar cambios.
  const validateServices = (): string | null => {
    if (formData.serviceIds.length === 0) {
      return "Selecciona al menos un servicio. Un profesional sin servicios no puede recibir citas.";
    }
    return null;
  };

  const goNext = () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        showToast(err, "error");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      const err = validateSchedule();
      if (err) {
        showToast(err, "error");
        return;
      }
      setStep(3);
    }
  };
  const goBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();

    const err1 = validateStep1();
    if (err1) {
      showToast(err1, "error");
      setStep(1);
      return;
    }
    const err2 = validateSchedule();
    if (err2) {
      showToast(err2, "error");
      setStep(2);
      return;
    }
    const err3 = validateServices();
    if (err3) {
      showToast(err3, "error");
      setStep(3);
      return;
    }

    const payload = {
      locationId: formData.locationId,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim() || null,
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      countryCode: formData.countryCode || "+34",
      color: formData.color,
      bio: formData.bio.trim() || null,
      isActive: formData.isActive,
      serviceIds: formData.serviceIds,
    };

    try {
      setIsSubmitting(true);
      let specialistId = editingId;
      if (editingId) {
        await apiClient.put(`/specialists/${editingId}`, payload);
      } else {
        const res = await apiClient.post("/specialists", payload);
        specialistId = res.data?.data?.id ?? null;
        // Si se seleccionó una foto en el wizard de creación (sin id
        // todavía), subirla ahora que ya tenemos el id del profesional
        // recién creado. Un fallo aquí no debe tratarse como que la
        // creación entera falló: el profesional ya existe.
        if (specialistId && modalPhotoFile) {
          try {
            const logo = await fileToDataUri(modalPhotoFile);
            await apiClient.post(`/specialists/${specialistId}/photo`, { logo });
          } catch (photoErr) {
            showToast(
              extractApiErrorMessage(
                photoErr,
                "El profesional se creó, pero no se pudo subir la foto. Podrás añadirla después editándolo.",
              ),
              "error",
            );
          }
        }
      }
      // Guardar horario semanal
      let scheduleFriendlyMessage = false;
      if (specialistId) {
        try {
          await apiClient.put(`/specialists/${specialistId}/schedule`, {
            days: scheduleDays,
          });
        } catch (schedErr) {
          const schedStatus = (
            schedErr as { response?: { status?: number } }
          )?.response?.status;
          if (schedStatus === 403) {
            // Falta el permiso citas:gestionar (distinto de servicios:editar,
            // que es el que se usó para crear/editar al profesional). No es
            // un fallo real: el horario simplemente se configurará más
            // tarde desde la agenda del profesional.
            scheduleFriendlyMessage = true;
          } else {
            console.error("Error al guardar el horario:", schedErr);
            showToast(
              extractApiErrorMessage(
                schedErr,
                "El profesional se guardó, pero hubo un error con el horario.",
              ),
              "error",
            );
          }
        }
      }
      showToast(
        scheduleFriendlyMessage
          ? "El profesional se " +
              (editingId ? "actualizó" : "creó") +
              " correctamente. Su horario se podrá configurar desde su agenda."
          : editingId
            ? "Profesional actualizado"
            : "Profesional creado",
        "success",
      );
      setShowModal(false);
      await loadAll();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar el profesional"),
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (s: Specialist) => {
    setTogglingId(s.id);
    try {
      await apiClient.put(`/specialists/${s.id}`, { isActive: !s.isActive });
      await loadAll();
    } catch (err) {
      showToast(
        extractApiErrorMessage(
          err,
          "No se pudo cambiar el estado del profesional",
        ),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (s: Specialist) => {
    const fullName = `${s.firstName} ${s.lastName ?? ""}`.trim();
    if (
      !(await confirm({
        title: `¿Eliminar al profesional "${fullName}"?`,
        message: "Esta acción no se puede deshacer.",
        confirmText: "Sí, eliminar",
        danger: true,
      }))
    )
      return;
    try {
      setDeletingId(s.id);
      await apiClient.delete(`/specialists/${s.id}`);
      showToast("Profesional eliminado");
      await loadAll();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo eliminar el profesional"),
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  };

  /** Mueve un profesional una posición arriba/abajo dentro de `specialists`
   * (ordenado por `sortOrder` desde el backend) y persiste el nuevo orden
   * completo con `PUT /specialists/reorder`. Optimista + recarga desde el
   * servidor si falla (mismo patrón que `handleMoveService` en
   * Services.tsx). Deshabilitado mientras hay una búsqueda activa. */
  const handleMoveSpecialist = async (
    specialistId: string,
    direction: "up" | "down",
  ) => {
    const idx = specialists.findIndex((s) => s.id === specialistId);
    if (idx === -1) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= specialists.length) return;

    const reordered = [...specialists];
    [reordered[idx], reordered[targetIdx]] = [
      reordered[targetIdx],
      reordered[idx],
    ];
    const specialistIds = reordered.map((s) => s.id);

    setReorderingId(specialistId);
    setSpecialists(reordered);
    try {
      await apiClient.put("/specialists/reorder", { specialistIds });
      await loadAll();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo reordenar el profesional"),
        "error",
      );
      await loadAll();
    } finally {
      setReorderingId(null);
    }
  };

  const locationName = (id: string): string =>
    locations.find((l) => l.id === id)?.name ?? "—";

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCircle2 className="w-6 h-6 text-indigo-600" />
            Profesionales
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Personas que prestan servicios y reciben citas
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={services.length === 0 || !canManage}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            !canManage
              ? "No tienes permiso para editar servicios"
              : services.length === 0
                ? "Crea primero al menos un servicio"
                : undefined
          }
        >
          <Plus className="w-4 h-4" />
          Nuevo profesional
        </button>
      </div>

      {services.length === 0 && !isLoading && !loadError && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Para asignar servicios a tus profesionales primero crea al menos uno
            en{" "}
            <a href="/services" className="underline font-medium">
              Servicios
            </a>
            .
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          Mostrar inactivos
        </label>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loadError ? (
          <div className="p-12 flex flex-col items-center justify-center gap-4 text-center">
            {loadError.isBookingModeMismatch ? (
              <>
                <CalendarX className="w-10 h-10 text-gray-400" />
                <p className="text-gray-600 max-w-sm">{loadError.message}</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  Volver al panel
                </Link>
              </>
            ) : (
              <>
                <AlertTriangle className="w-10 h-10 text-red-500" />
                <p className="text-red-600 max-w-sm">{loadError.message}</p>
                <button
                  onClick={loadAll}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reintentar
                </button>
              </>
            )}
          </div>
        ) : isLoading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <UserCircle2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">
              {specialists.length === 0
                ? "Aún no tienes profesionales"
                : "No hay profesionales que coincidan con la búsqueda"}
            </p>
            {specialists.length === 0 && services.length > 0 && (
              <p className="text-sm mt-1">
                Crea el primero para empezar a gestionar citas.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((s) => {
                const orderIdx = specialists.findIndex((x) => x.id === s.id);
                const isFirst = orderIdx <= 0;
                const isLast =
                  orderIdx === -1 || orderIdx === specialists.length - 1;
                const reorderBlocked =
                  !canManage || !!search.trim() || reorderingId !== null;
                const reorderTitle = search.trim()
                  ? "Quita la búsqueda para reordenar"
                  : undefined;
                return (
                <div key={s.id} className="p-4">
                  <div className="flex items-start gap-3">
                    {s.avatar ? (
                      <img
                        src={s.avatar}
                        alt=""
                        className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold"
                        style={{ backgroundColor: s.color }}
                      >
                        {s.firstName.charAt(0).toUpperCase()}
                        {(s.lastName ?? "").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">
                          {s.firstName} {s.lastName ?? ""}
                        </p>
                        {!s.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 uppercase">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {locationName(s.locationId)}
                      </p>
                      {(s.email || s.phone) && (
                        <div className="flex flex-col gap-0.5 mt-1 text-xs text-gray-600">
                          {s.email && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {s.email}
                            </span>
                          )}
                          {s.phone && (
                            <span className="inline-flex items-center gap-1">
                              <PhoneIcon className="w-3 h-3" />
                              {s.countryCode} {s.phone}
                            </span>
                          )}
                        </div>
                      )}
                      {s.services.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {s.services.length} servicio
                          {s.services.length === 1 ? "" : "s"}
                        </p>
                      )}
                      {/* Google Calendar status (mobile) */}
                      <GoogleCalendarRow
                        status={gcStatus[s.id]}
                        busy={gcBusy[s.id]}
                        canManage={canManage}
                        onConnect={() => handleConnectGoogle(s.id)}
                        onSync={() => handleSyncGoogle(s.id)}
                        onDisconnect={() => handleDisconnectGoogle(s.id)}
                        onRefresh={() => refreshGcStatus(s.id)}
                      />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleMoveSpecialist(s.id, "up")}
                          disabled={reorderBlocked || isFirst}
                          title={reorderTitle ?? "Subir"}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {reorderingId === s.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ArrowUp className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleMoveSpecialist(s.id, "down")}
                          disabled={reorderBlocked || isLast}
                          title={reorderTitle ?? "Bajar"}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() =>
                          navigate(`/specialists/${s.id}/schedule`)
                        }
                        className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600"
                        title="Horario"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(s)}
                        disabled={!canManage || togglingId === s.id}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          !canManage
                            ? "No tienes permiso para editar servicios"
                            : s.isActive
                              ? "Ocultar"
                              : "Activar"
                        }
                      >
                        {togglingId === s.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : s.isActive ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEdit(s)}
                        disabled={!canManage}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={!canManage}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingId === s.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">
                      Profesional
                    </th>
                    <th className="text-left px-4 py-3 font-medium">
                      Ubicación
                    </th>
                    <th className="text-left px-4 py-3 font-medium">
                      Contacto
                    </th>
                    <th className="text-left px-4 py-3 font-medium">
                      Servicios
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Estado</th>
                    <th className="text-right px-4 py-3 font-medium">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((s) => {
                    const orderIdx = specialists.findIndex(
                      (x) => x.id === s.id,
                    );
                    const isFirst = orderIdx <= 0;
                    const isLast =
                      orderIdx === -1 || orderIdx === specialists.length - 1;
                    const reorderBlocked =
                      !canManage || !!search.trim() || reorderingId !== null;
                    const reorderTitle = search.trim()
                      ? "Quita la búsqueda para reordenar"
                      : undefined;
                    return (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {s.avatar ? (
                            <img
                              src={s.avatar}
                              alt=""
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs"
                              style={{ backgroundColor: s.color }}
                            >
                              {s.firstName.charAt(0).toUpperCase()}
                              {(s.lastName ?? "").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">
                              {s.firstName} {s.lastName ?? ""}
                            </p>
                            {s.bio && (
                              <p className="text-xs text-gray-500 truncate max-w-xs">
                                {s.bio}
                              </p>
                            )}
                            {/* Google Calendar (desktop) */}
                            <GoogleCalendarRow
                              status={gcStatus[s.id]}
                              busy={gcBusy[s.id]}
                              canManage={canManage}
                              onConnect={() => handleConnectGoogle(s.id)}
                              onSync={() => handleSyncGoogle(s.id)}
                              onDisconnect={() => handleDisconnectGoogle(s.id)}
                              onRefresh={() => refreshGcStatus(s.id)}
                              compact
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {locationName(s.locationId)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.email && (
                          <div className="text-xs truncate max-w-[180px]">
                            {s.email}
                          </div>
                        )}
                        {s.phone && (
                          <div className="text-xs text-gray-500">
                            {s.countryCode} {s.phone}
                          </div>
                        )}
                        {!s.email && !s.phone && (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {s.services.length === 0 ? (
                          <span className="text-xs text-gray-400">
                            Sin servicios
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {s.services.slice(0, 3).map((ss) => (
                              <span
                                key={ss.id}
                                className="text-[10px] px-2 py-0.5 rounded-full text-white"
                                style={{
                                  backgroundColor:
                                    ss.service?.color || "#6366f1",
                                }}
                              >
                                {ss.service?.name ?? ""}
                              </span>
                            ))}
                            {s.services.length > 3 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                +{s.services.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {s.isActive ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => handleMoveSpecialist(s.id, "up")}
                            disabled={reorderBlocked || isFirst}
                            title={reorderTitle ?? "Subir"}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {reorderingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ArrowUp className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleMoveSpecialist(s.id, "down")}
                            disabled={reorderBlocked || isLast}
                            title={reorderTitle ?? "Bajar"}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/specialists/${s.id}/schedule`)
                            }
                            className="p-2 hover:bg-indigo-50 rounded-lg text-indigo-600"
                            title="Horario"
                          >
                            <Clock className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleActive(s)}
                            disabled={!canManage || togglingId === s.id}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              !canManage
                                ? "No tienes permiso para editar servicios"
                                : s.isActive
                                  ? "Ocultar"
                                  : "Activar"
                            }
                          >
                            {togglingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : s.isActive ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openEdit(s)}
                            disabled={!canManage}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Editar"
                                : "No tienes permiso para editar servicios"
                            }
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(s)}
                            disabled={!canManage}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Eliminar"
                                : "No tienes permiso para editar servicios"
                            }
                          >
                            {deletingId === s.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Wizard */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-2xl md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] flex flex-col">
            {/* Header con stepper */}
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingId ? "Editar profesional" : "Nuevo profesional"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Cerrar"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {[
                  { n: 1, label: "Datos" },
                  { n: 2, label: "Horario" },
                  { n: 3, label: "Servicios" },
                ].map((s, i) => (
                  <div key={s.n} className="flex items-center gap-2 flex-1">
                    <div
                      className={`flex items-center gap-2 ${
                        step === s.n
                          ? "text-indigo-600 font-semibold"
                          : step > s.n
                            ? "text-emerald-600"
                            : "text-gray-400"
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] border ${
                          step === s.n
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : step > s.n
                              ? "bg-emerald-500 text-white border-emerald-500"
                              : "bg-white text-gray-500 border-gray-300"
                        }`}
                      >
                        {s.n}
                      </span>
                      <span className="hidden sm:inline">{s.label}</span>
                    </div>
                    {i < 2 && (
                      <div
                        className={`flex-1 h-px ${
                          step > s.n ? "bg-emerald-400" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto p-5 space-y-4"
            >
              {/* PASO 1 - DATOS */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="relative group flex-shrink-0">
                      {modalAvatarUrl ? (
                        <img
                          src={modalAvatarUrl}
                          alt="Foto del profesional"
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                          style={{ backgroundColor: formData.color }}
                        >
                          {formData.firstName ? (
                            <>
                              {formData.firstName.charAt(0).toUpperCase()}
                              {formData.lastName.charAt(0).toUpperCase()}
                            </>
                          ) : (
                            <UserCircle2 className="w-7 h-7" />
                          )}
                        </div>
                      )}
                      <label
                        className={`absolute inset-0 rounded-full bg-black/50 flex items-center justify-center cursor-pointer transition-opacity ${
                          photoUploading
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                        title={modalAvatarUrl ? "Cambiar foto" : "Subir foto"}
                      >
                        {photoUploading ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5 text-white" />
                        )}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          className="hidden"
                          onChange={handlePhotoFileChange}
                          disabled={photoUploading || !canManage}
                        />
                      </label>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Foto del profesional
                      </p>
                      <p className="text-xs text-gray-500">
                        {editingId
                          ? "Se sube al instante. PNG, JPEG, GIF o WebP."
                          : "Se subirá al guardar. PNG, JPEG, GIF o WebP."}
                      </p>
                      {modalAvatarUrl && canManage && (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          disabled={photoUploading}
                          className="text-xs text-red-600 hover:text-red-700 mt-1 disabled:opacity-50"
                        >
                          Quitar foto
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ubicación <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.locationId}
                      onChange={(e) =>
                        setFormData({ ...formData, locationId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    >
                      <option value="">Selecciona una ubicación</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Apellido
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            lastName: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pref.
                      </label>
                      <input
                        type="text"
                        value={formData.countryCode}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            countryCode: e.target.value,
                          })
                        }
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color en el calendario
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) =>
                          setFormData({ ...formData, color: e.target.value })
                        }
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <div className="flex flex-wrap gap-1">
                        {COLOR_PRESETS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() =>
                              setFormData({ ...formData, color: c })
                            }
                            className={`w-6 h-6 rounded-full border-2 ${
                              formData.color === c
                                ? "border-gray-900"
                                : "border-white"
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio / Descripción corta
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) =>
                        setFormData({ ...formData, bio: e.target.value })
                      }
                      rows={2}
                      placeholder="Especialidad, años de experiencia…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isActive: e.target.checked,
                        })
                      }
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Profesional activo
                  </label>
                </div>
              )}

              {/* PASO 2 - HORARIO */}
              {step === 2 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Define los días y horarios habituales. Podrás añadir
                      excepciones más tarde.
                    </p>
                    <button
                      type="button"
                      onClick={copyMondayToWeek}
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-md hover:bg-gray-50 text-gray-700"
                      title="Copiar lunes a martes-viernes"
                    >
                      <Copy className="w-3 h-3" /> Copiar lunes a L-V
                    </button>
                  </div>
                  <div className="space-y-2">
                    {DAY_ORDER.map((dow) => {
                      const day = scheduleDays.find(
                        (d) => d.dayOfWeek === dow,
                      )!;
                      return (
                        <div
                          key={dow}
                          className="border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={day.isWorking}
                                onChange={(e) =>
                                  updateDay(dow, {
                                    isWorking: e.target.checked,
                                    shifts:
                                      e.target.checked &&
                                      day.shifts.length === 0
                                        ? [{ start: "09:00", end: "13:00" }]
                                        : day.shifts,
                                  })
                                }
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm font-medium text-gray-800 w-24 inline-block">
                                {DAY_NAMES[dow]}
                              </span>
                              {!day.isWorking && (
                                <span className="text-xs text-gray-400">
                                  Cerrado
                                </span>
                              )}
                            </label>
                            {day.isWorking && (
                              <button
                                type="button"
                                onClick={() => addShift(dow)}
                                className="text-xs inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                              >
                                <Plus className="w-3 h-3" /> Turno
                              </button>
                            )}
                          </div>
                          {day.isWorking && (
                            <div className="space-y-2">
                              {day.shifts.map((sh, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="time"
                                    value={sh.start}
                                    onChange={(e) =>
                                      updateShift(dow, idx, {
                                        start: e.target.value,
                                      })
                                    }
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <span className="text-gray-400">—</span>
                                  <input
                                    type="time"
                                    value={sh.end}
                                    onChange={(e) =>
                                      updateShift(dow, idx, {
                                        end: e.target.value,
                                      })
                                    }
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeShift(dow, idx)}
                                    className="ml-auto p-1 text-gray-400 hover:text-red-600"
                                    aria-label="Eliminar turno"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                              {day.shifts.length === 0 && (
                                <p className="text-xs text-amber-600">
                                  Sin turnos. Añade uno o desmarca el día.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* PASO 3 - SERVICIOS */}
              {step === 3 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Servicios que ofrece
                  </label>
                  {formData.serviceIds.length === 0 && (
                    <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-xs text-amber-800">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Selecciona al menos un servicio. Un profesional sin
                        servicios no puede recibir citas y nunca aparecerá
                        disponible.
                      </span>
                    </div>
                  )}
                  {services.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      No hay servicios disponibles. Crea uno en /services.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {services.map((svc) => {
                        const checked = formData.serviceIds.includes(svc.id);
                        return (
                          <button
                            type="button"
                            key={svc.id}
                            onClick={() => toggleServiceId(svc.id)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                              checked
                                ? "text-white border-transparent"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            style={
                              checked
                                ? { backgroundColor: svc.color }
                                : undefined
                            }
                          >
                            {svc.name}
                            <span className="ml-1 opacity-70">
                              · {svc.durationMinutes}m
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-3">
                    Resumen:{" "}
                    <strong>
                      {formData.firstName} {formData.lastName}
                    </strong>
                    {" · "}
                    {scheduleDays.filter((d) => d.isWorking).length} día(s)
                    laborables
                    {" · "}
                    {formData.serviceIds.length} servicio(s) seleccionados
                  </p>
                </div>
              )}
            </form>

            {/* Footer con navegación */}
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={step === 1 ? () => setShowModal(false) : goBack}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {step === 1 ? (
                  "Cancelar"
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4" /> Atrás
                  </>
                )}
              </button>
              <span className="text-xs text-gray-400">Paso {step} de 3</span>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Siguiente <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    !canManage ||
                    formData.serviceIds.length === 0
                  }
                  title={
                    formData.serviceIds.length === 0
                      ? "Selecciona al menos un servicio para poder guardar"
                      : undefined
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingId ? "Guardar cambios" : "Crear profesional"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: enlace de invitación para conectar Google Calendar */}
      {inviteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setInviteModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Conectar Google Calendar
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Para{" "}
                  <span className="font-medium text-gray-700">
                    {inviteModal.specialist.firstName}{" "}
                    {inviteModal.specialist.lastName ?? ""}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInviteModal(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                aria-label="Cerrar"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm text-indigo-900">
                Envía este enlace al profesional para que conecte su cuenta de
                Google. Solo tendrá que pulsar y aceptar los permisos. No
                necesita crear ninguna cuenta en ReservaManager.
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Enlace de invitación
                </label>
                <div className="flex gap-2">
                  <input
                    id="invite-link-input"
                    type="text"
                    readOnly
                    value={inviteModal.url}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 text-xs bg-gray-50 border border-gray-300 rounded-lg font-mono text-gray-700"
                  />
                  <button
                    type="button"
                    onClick={copyInviteLink}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border whitespace-nowrap ${
                      copyState === "copied"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {copyState === "copied" ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5">
                  El enlace caduca en {inviteModal.expiresInMinutes} minutos. Si
                  caduca, vuelve a generarlo.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <button
                  type="button"
                  onClick={sendInviteByEmail}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Mail className="w-4 h-4" />
                  Enviar por email
                  {inviteModal.specialist.email
                    ? ` a ${inviteModal.specialist.email}`
                    : ""}
                </button>
                {!inviteModal.specialist.email && (
                  <p className="text-[11px] text-gray-500 mt-1.5 text-center">
                    Este profesional no tiene email registrado. Se abrirá tu
                    cliente de correo en blanco para que añadas el destinatario.
                  </p>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end">
              <button
                type="button"
                onClick={() => setInviteModal(null)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Sub-componente: estado y acciones de Google Calendar por especialista
// ============================================================
interface GoogleCalendarRowProps {
  status:
    | {
        connected: boolean;
        googleEmail?: string | null;
        googleCalendarId?: string | null;
        syncStatus?: string | null;
        lastError?: string | null;
        lastSyncAt?: string | null;
      }
    | undefined;
  busy: string | undefined; // 'connect' | 'sync' | 'disconnect'
  /** `true` si el usuario puede conectar/sincronizar/desconectar (permiso `servicios:editar`). */
  canManage: boolean;
  onConnect: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  onRefresh: () => void;
  compact?: boolean;
}

function GoogleCalendarRow({
  status,
  busy,
  canManage,
  onConnect,
  onSync,
  onDisconnect,
  onRefresh,
  compact,
}: GoogleCalendarRowProps) {
  const sizeClass = compact ? "text-[11px]" : "text-xs";
  const iconSize = compact ? "w-3 h-3" : "w-3.5 h-3.5";

  // Aún cargando estado
  if (!status) {
    return (
      <div
        className={`mt-2 flex items-center gap-1.5 text-gray-400 ${sizeClass}`}
      >
        <CalendarIcon className={iconSize} />
        <span>Cargando estado de Google Calendar…</span>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className={`mt-2 flex items-center gap-2 ${sizeClass}`}>
        <span className="inline-flex items-center gap-1 text-gray-500">
          <CalendarIcon className={iconSize} />
          Google Calendar:
        </span>
        <button
          type="button"
          onClick={onConnect}
          disabled={busy === "connect" || !canManage}
          title={canManage ? undefined : "No tienes permiso para editar servicios"}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
        >
          {busy === "connect" ? (
            <Loader2 className={`${iconSize} animate-spin`} />
          ) : (
            <LinkIcon className={iconSize} />
          )}
          Conectar
        </button>
      </div>
    );
  }

  // Conectado (puede estar en error si los tokens caducaron o hubo fallo)
  const isError = status.syncStatus === "error";
  return (
    <div
      className={`mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 ${sizeClass}`}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          isError ? "text-amber-700" : "text-emerald-700"
        }`}
      >
        {isError ? (
          <AlertCircle className={iconSize} />
        ) : (
          <CheckCircle2 className={iconSize} />
        )}
        Google Calendar
        {status.googleEmail ? ` · ${status.googleEmail}` : ""}
      </span>
      {isError && status.lastError && (
        <span className="text-amber-700 italic">({status.lastError})</span>
      )}
      <button
        type="button"
        onClick={onSync}
        disabled={busy === "sync" || !canManage}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        title={canManage ? "Sincronizar ahora" : "No tienes permiso para editar servicios"}
      >
        {busy === "sync" ? (
          <Loader2 className={`${iconSize} animate-spin`} />
        ) : (
          <RefreshCw className={iconSize} />
        )}
        Sincronizar
      </button>
      <button
        type="button"
        onClick={onDisconnect}
        disabled={busy === "disconnect" || !canManage}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
        title={canManage ? "Desconectar Google Calendar" : "No tienes permiso para editar servicios"}
      >
        {busy === "disconnect" ? (
          <Loader2 className={`${iconSize} animate-spin`} />
        ) : (
          <Unlink className={iconSize} />
        )}
        Desconectar
      </button>
      <button
        type="button"
        onClick={onRefresh}
        className="text-gray-400 hover:text-gray-600"
        title="Actualizar estado"
      >
        <RefreshCw className={iconSize} />
      </button>
    </div>
  );
}
